
import { useEffect } from "react";
import { 
  HeroSection, 
  BenefitsSection, 
  LoanTypesSection, 
  LoanExamplesSection, 
  PromotionalSection,
  NewsletterSection
} from "@/components/home";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PienlainatPromotion } from "@/components/loan/PienlainatPromotion";
import { useIsMobile } from "@/hooks/use-mobile";
import { YrityslainatPromotion } from "@/components/loan/YrityslainatPromotion";
import { YhdistelylainaPromotion } from "@/components/loan/YhdistelylainaPromotion";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute, saveEnhancedSeoMetadata } from "@/utils/enhancedSeoUtils";
import CreditstarAd from "@/components/ads/CreditstarAd";
import CreditstarPopup from "@/components/ads/CreditstarPopup";

const Index = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const seoData = getEnhancedSeoDataForRoute("/");
  
  // Critical image preloading - only the most important images
  useEffect(() => {
    const preloadCriticalImages = () => {
      if (typeof window === 'undefined') return;
      
      const criticalImages = [
        // Hero section images - optimize for mobile
        '/lovable-uploads/8bfd2c56-af00-4b88-ae7f-7d61484d4472.png',
        '/lovable-uploads/cb302d8d-e104-44b7-9de6-77fe6f301e21.png'
      ];
      
      criticalImages.forEach(src => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        link.fetchPriority = 'high';
        // Add responsive preloading for mobile
        if (window.innerWidth < 768) {
          link.media = '(max-width: 767px)';
        }
        document.head.appendChild(link);
      });
    };
    
    // Preload immediately for better performance
    preloadCriticalImages();
  }, []);

  // Auto-save SEO data to Supabase when page loads
  useEffect(() => {
    const autoSaveSeoData = async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        
        if (session?.session) {
          // If user is authenticated, save current page SEO data
          await saveEnhancedSeoMetadata("/", seoData);
          console.log('SEO data auto-saved to Supabase for homepage');
        }
      } catch (error) {
        console.error('Error auto-saving SEO data:', error);
      }
    };

    autoSaveSeoData();
  }, [seoData]);

  return (
    <>
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}  
        canonicalUrl="https://lainasampo.fi/"
        schema={seoData.schema}
      />
      
      <div className="bg-blue-50/30 min-h-screen">
        <HeroSection />
        
        {/* Creditstar Advertisement - moved higher for mobile */}
        <div className="container mx-auto py-6 md:py-8 px-4">
          <CreditstarAd />
        </div>
        
        <BenefitsSection />
        
        {/* Enhanced promotion area - pienlainat, yrityslaina ja yhdistelylaina ads */}
        <div className="container mx-auto py-8 px-4">
          {isMobile ? (
            <div className="space-y-6">
              <PienlainatPromotion />
              <YrityslainatPromotion variant="default" />
              <YhdistelylainaPromotion variant="default" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="h-full">
                <PienlainatPromotion />
              </div>
              <div className="h-full">
                <YrityslainatPromotion variant="default" />
              </div>
              <div className="h-full">
                <YhdistelylainaPromotion variant="default" />
              </div>
            </div>
          )}
        </div>
        
        <LoanTypesSection />
        <LoanExamplesSection />
        <PromotionalSection />
        
        <div className="py-12 bg-gray-50">
          <NewsletterSection />
        </div>
      </div>
      
      <CreditstarPopup />
    </>
  );
};

export default Index;
